import type { Contract, GlobalMetadatum, JsonSchemaType } from '@gmn-portal/backend';
import type { TFunction } from 'i18next';

/**
 * Gets the display name for a field in activity logs
 * @param field The field path (e.g., 'metadata.key', 'dynamicMetadata.key', 'title', 'status')
 * @param contract The contract document (for dynamic metadata schema)
 * @param globalMetadata The global metadata configuration
 * @param t Translation function
 * @returns The field display name or null if field is deleted/inaccessible
 */
export function getFieldName(
  field: string,
  contract?: Contract,
  globalMetadata?: GlobalMetadatum,
  t?: TFunction<'compat'>
): string | null {
  // Handle regular fields
  if (!field.startsWith('metadata.') && !field.startsWith('dynamicMetadata.')) {
    return t
      ? (t(`activity.contract.fields.${field}` as unknown as never) as string)
      : `activity.contract.fields.${field}`;
  }

  // Handle metadata fields
  if (field.startsWith('metadata.')) {
    const [, key] = field.split('.');
    const metadataSchemaToUse = globalMetadata?.rootContractMetadata as JsonSchemaType | undefined;

    if (!metadataSchemaToUse?.properties?.[key]) {
      return null; // Field has been deleted from schema
    }

    const description = metadataSchemaToUse.properties[key].description;
    return description || key;
  }

  // Handle dynamicMetadata fields
  if (field.startsWith('dynamicMetadata.')) {
    const [, key] = field.split('.');

    if (!contract?.dynamicMetadataSchema?.properties?.[key]) {
      return null; // Field has been deleted from schema
    }

    const description = contract.dynamicMetadataSchema.properties[key].description;
    return description || key;
  }

  return null;
}
